import {
  ALLOWED_PERSONAL_TEST_EMAILS,
  CONSUMER_EMAIL_DOMAINS,
} from "@Aspecto/backend/convex/consumerEmailDomains";
import { useState, type FormEvent, type ReactNode } from "react";

import { Button } from "@/components/ui/button";

export const WORK_EMAIL_ERROR =
  "Please use your work email address. Personal email providers like Gmail, Outlook, Yahoo, and iCloud are not supported.";

export function isConsumerEmail(email: string | null | undefined) {
  const normalizedEmail = email?.trim().toLowerCase();
  if (normalizedEmail && ALLOWED_PERSONAL_TEST_EMAILS.has(normalizedEmail)) {
    return false;
  }

  const domain = email?.split("@")[1]?.trim().toLowerCase();
  return !!domain && CONSUMER_EMAIL_DOMAINS.has(domain);
}

function getSubmittedEmail(form: HTMLFormElement) {
  const formData = new FormData(form);
  for (const value of formData.values()) {
    if (typeof value === "string" && value.includes("@")) {
      return value.trim();
    }
  }

  const input = form.querySelector<HTMLInputElement>(
    'input[type="email"], input[name*="email" i], input[name="identifier"]',
  );
  return input?.value.trim() ?? "";
}

export function guardWorkEmailSubmit(
  event: FormEvent<HTMLElement>,
  onBlocked: (message: string) => void,
) {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form) return;

  if (!isConsumerEmail(getSubmittedEmail(form))) {
    onBlocked("");
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  onBlocked(WORK_EMAIL_ERROR);
}

interface WorkEmailErrorProps {
  message: string | null;
}

export function WorkEmailError({ message }: WorkEmailErrorProps) {
  if (!message) return null;

  return (
    <p
      className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
      role="alert"
    >
      {message}
    </p>
  );
}

interface WorkEmailFormGuardProps {
  children: ReactNode;
  className?: string;
}

export function WorkEmailFormGuard({ children, className }: WorkEmailFormGuardProps) {
  const [message, setMessage] = useState("");

  return (
    <div className={className} onSubmitCapture={(event) => guardWorkEmailSubmit(event, setMessage)}>
      {children}
      <WorkEmailError message={message} />
    </div>
  );
}

interface ConsumerEmailBlockedCardProps {
  email?: string | null;
  onSignOut: () => void;
}

export function ConsumerEmailBlockedCard({ email, onSignOut }: ConsumerEmailBlockedCardProps) {
  return (
    <div className="flex flex-1 items-center justify-center px-4">
      <div className="w-full max-w-[25rem] rounded-2xl border bg-card px-8 py-10 text-center shadow-sm">
        <h1 className="text-xl font-semibold tracking-tight">Use your work email</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {email ? `${email} is a personal email address. ` : ""}
          Afinio accounts must be created with a business or organization email address.
        </p>
        <Button className="mt-6 w-full" variant="outline" onClick={onSignOut}>
          Sign out and try again
        </Button>
      </div>
    </div>
  );
}
